import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import {
  HubPage, Container, ResourceCard, FeaturedCard, Disclaimer, CommunityCTA, BetaCTA, Band, HeroMedia, Seo,
} from "../components/hub/HubKit";
import {
  categoryBySlug, resourcesByCategory, needsDisclaimer, relatedCategories, typeLabel,
} from "../data/hub";

export default function Category() {
  const { slug } = useParams();
  const cat = categoryBySlug(slug);
  const [activeType, setActiveType] = useState("all");
  if (!cat) return <Navigate to="/resources" replace />;

  const items = resourcesByCategory(slug);
  const featured = items[0];
  const rest = items.slice(1);
  const typesInCat = [...new Set(items.map((r) => r.type))];
  const filtered = activeType === "all" ? rest : items.filter((r) => r.type === activeType);
  const related = relatedCategories(slug);
  const a = cat.accent || { tint: "#e9f0e6", deep: "#3E5A44" };
  const Icon = cat.icon;

  return (
    <HubPage testid={`category-page-${slug}`}>
      <Seo title={`${cat.title} — Everyday Resources | HEARD`} description={cat.intro} />

      {/* Accent hero — category-specific colour + editorial photograph */}
      <Band className="relative overflow-hidden border-b border-line/60">
        <div className="absolute inset-0" style={{ background: `linear-gradient(160deg, ${a.tint} 0%, transparent 62%)` }} aria-hidden="true" />
        <Container className="relative pt-14 pb-12 sm:pt-20">
          <div className="grid items-center gap-8 lg:grid-cols-[1fr_0.82fr]">
            <div>
              <Link to="/resources" className="inline-flex items-center gap-1.5 text-sm hover:opacity-70" style={{ color: a.deep }} data-testid="back-to-resources">
                <ArrowLeft size={15} /> All resources
              </Link>
              <div className="mt-6 flex items-center gap-4">
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl" style={{ backgroundColor: a.tint, color: a.deep }}>
                  <Icon size={30} strokeWidth={1.6} />
                </span>
                <div>
                  <span className="text-[0.7rem] uppercase tracking-[0.24em]" style={{ color: a.deep }}>Resource Category</span>
                  <h1 className="serif-display text-[2rem] sm:text-[2.7rem] font-normal leading-tight text-charcoal">{cat.title}</h1>
                </div>
              </div>
              <p className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-ink/75">{cat.intro}</p>
              {needsDisclaimer(slug) && <div className="mt-8 max-w-xl"><Disclaimer /></div>}
            </div>
            {cat.image && (
              <HeroMedia src={cat.image} alt={`${cat.title} — a calm, warm editorial photograph`} eager className="aspect-[16/10] lg:aspect-[5/4]" />
            )}
          </div>
        </Container>
      </Band>

      {/* Featured resource */}
      {featured && (
        <Container className="py-14 sm:py-16">
          <span className="text-[0.62rem] font-semibold uppercase tracking-[0.22em]" style={{ color: a.deep }}>Featured in this category</span>
          <div className="mt-4">
            <FeaturedCard resource={featured} eyebrow="Start here" testid={`category-featured-${featured.slug}`} />
          </div>
        </Container>
      )}

      {/* More in category */}
      {rest.length > 0 && (
        <Band tone="white" className="border-y border-line/60 py-14 sm:py-20">
          <Container>
            {typesInCat.length > 1 && (
              <div className="flex flex-wrap gap-2.5" data-testid="category-type-filters">
                <button
                  onClick={() => setActiveType("all")}
                  data-testid="category-filter-all"
                  className={`rounded-full border px-4 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.16em] transition-colors ${
                    activeType === "all" ? "border-sage-deep bg-sage-deep text-cream" : "border-line bg-white/70 text-sage-deep hover:border-sage"
                  }`}
                >
                  All
                </button>
                {typesInCat.map((t) => (
                  <button
                    key={t}
                    onClick={() => setActiveType(t)}
                    data-testid={`category-filter-${t}`}
                    className={`rounded-full border px-4 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.16em] transition-colors ${
                      activeType === t ? "border-sage-deep bg-sage-deep text-cream" : "border-line bg-white/70 text-sage-deep hover:border-sage"
                    }`}
                  >
                    {typeLabel(t)}
                  </button>
                ))}
              </div>
            )}
            <h2 className="serif-display mt-8 text-2xl text-charcoal">More in {cat.title}</h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="category-resource-grid">
              {filtered.length > 0
                ? filtered.map((r) => <ResourceCard key={r.slug} resource={r} />)
                : <p className="text-ink/70">More {typeLabel(activeType)} guides for this area are on the way.</p>}
            </div>
          </Container>
        </Band>
      )}

      {items.length === 0 && (
        <Container className="py-10"><p className="text-ink/70">New guides for this area are on the way.</p></Container>
      )}

      {/* Related categories */}
      {related.length > 0 && (
        <Container className="py-14 sm:py-16">
          <div data-testid="related-categories">
            <h2 className="serif-display text-2xl text-charcoal">Related categories</h2>
            <div className="mt-6 flex flex-wrap gap-3">
              {related.map((rc) => {
                const ra = rc.accent || a;
                return (
                  <Link
                    key={rc.slug}
                    to={`/resources/${rc.slug}`}
                    data-testid={`related-category-${rc.slug}`}
                    className="group inline-flex items-center gap-2 rounded-full border border-line bg-white/70 px-5 py-3 text-sm font-medium text-charcoal transition-all hover:border-sage hover:bg-white"
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-full" style={{ backgroundColor: ra.tint, color: ra.deep }}>
                      <rc.icon size={14} strokeWidth={1.9} />
                    </span>
                    {rc.title}
                    <ArrowRight size={13} className="opacity-0 transition-opacity group-hover:opacity-100" style={{ color: ra.deep }} />
                  </Link>
                );
              })}
            </div>
          </div>
        </Container>
      )}

      <Band tone="deep" className="py-14 sm:py-20">
        <Container>
          <BetaCTA />
          <div className="mt-8"><CommunityCTA /></div>
        </Container>
      </Band>
    </HubPage>
  );
}
